import { AfterViewInit, Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormGroupDirective,
  NgForm,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { FeatureFlagService } from '@vendasta/businesses';
import { Market } from '@vendasta/businesses/easy-account-create';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { map, shareReplay, startWith, switchMap, take, tap } from 'rxjs/operators';
import { ActionListsStoreService } from '../action-lists';
import { ActionList } from '../action-lists/action-list';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { FieldSelectedForEditService as CreateOrEditCustomFieldService } from '@vendasta/auxiliary-data-components'; // nx-ignore
import { WELCOME_EMAIL_LOCALES } from '../constants';
import { AccessService, Feature, StubAccessService } from '../core/access';
import { MarketsService } from '../core/markets/markets.service';
import { DynamicOpenCloseTemplateRefService } from '../side-drawer/dynamic-open-close-template-ref.service';
import { PreviewWelcomeEmailComponent } from '../users/user/preview-welcome-email-dialog.component';
import { VConfigService } from '../vconfig/vconfig.service';
import { BulkImportMappingService } from './bulk-import-mapping.service';
import { BulkImportService } from './bulk-import.service';

@Component({
  templateUrl: './bulk-import.component.html',
  styleUrls: ['./bulk-import.component.scss'],
  standalone: false,
})
export class BulkImportComponent implements OnInit, AfterViewInit, OnDestroy {
  secondFormGroup: UntypedFormGroup;
  fileTypes = ['text/csv'];
  marketUiESM: MyErrorStateMatcher;
  filteredMarkets$: Observable<Market[]>;
  marketSearchTerm$$: BehaviorSubject<string> = new BehaviorSubject('');
  listSearchTerm$$: BehaviorSubject<string> = new BehaviorSubject('');
  selectFirstMarket = false;
  hasAccessToMarkets$: Observable<boolean>;
  marketUiFormControl: UntypedFormControl;
  fileFormControl: UntypedFormControl;
  listFormControl: UntypedFormControl;
  filteredLists$: Observable<ActionList[]>;
  selectFirstActionList: boolean;
  subscriptions: Subscription[] = [];
  accessibleMarkets: string[] = [];
  totalRows$: Observable<number>;
  marketName: string;
  listName: string;
  fileName: string;
  errorRows: any = [];
  show = 10;
  listText = `To an <strong>existing list</strong> named:`;
  fileForm: UntypedFormGroup;
  marketListForm: UntypedFormGroup;
  listSelected = false;
  marketMatcher: MyErrorStateMatcher;
  listMatcher: MyErrorStateMatcher;
  @ViewChild('stepper', { static: true })
  pageStepper: MatStepper;
  processing: boolean;
  processingErrorMessage: string = null;
  loading = false;
  netRows$: Observable<number>;
  marketAndListText = 'Choose List';
  overwriteBlank = true;
  partnerBusinessCenterName$: Observable<string>;
  permissionsFlag: boolean;
  notificationsFlag: boolean;
  welcomeEmailFlag: boolean;
  emailLanguage: string;
  welcomeMessage: string;
  emailLanguages = WELCOME_EMAIL_LOCALES;
  englishSelection: string;
  vbcConfigKey = 'vbc_product_name';
  @ViewChild('customFieldEditor', { static: true }) customFieldEditor: TemplateRef<any>;

  constructor(
    private formBuilder: UntypedFormBuilder,
    public dialog: MatDialog,
    private marketService: MarketsService,
    private actionListsStoreService: ActionListsStoreService,
    private bulkImportService: BulkImportService,
    private mappingService: BulkImportMappingService,
    @Inject(StubAccessService) private accessService: AccessService,
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    @Inject('MARKET_ID') private readonly marketId$: Observable<string>,
    private feature: FeatureFlagService,
    private router: Router,
    private vConfigService: VConfigService,
    private readonly createOrEditCustomFieldService: CreateOrEditCustomFieldService,
    private readonly dynamicOpenCloseService: DynamicOpenCloseTemplateRefService,
    private readonly trackingService: ProductAnalyticsService,
  ) {}

  get filename(): string {
    if (this.bulkImportService.file) {
      return this.bulkImportService.file.name;
    }
    return '';
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.createOrEditCustomFieldService.createClicked$
        .pipe(tap(() => this.dynamicOpenCloseService.open('customFieldEditor')))
        .subscribe(),
    );
    this.actionListsStoreService.initializeStore(1000);
    this.marketUiFormControl = new UntypedFormControl('');
    this.fileFormControl = new UntypedFormControl('', [Validators.required]);
    this.listFormControl = new UntypedFormControl('', [Validators.required]);
    this.secondFormGroup = this.formBuilder.group({
      secondCtrl: ['', Validators.required],
    });

    this.fileForm = this.formBuilder.group({
      file: ['', Validators.required],
      fileHasErrors: [false],
    });

    this.marketListForm = this.formBuilder.group({
      marketId: [null, Validators.required],
      actionListId: [null, Validators.required],
      actionListName: [''],
      useExistingList: [true],
    });

    this.hasAccessToMarkets$ = this.accessService.hasAccessToFeature(Feature.markets).pipe(
      tap((hasAccess) => {
        hasAccess ? (this.marketAndListText = 'Choose Market and List') : (this.marketAndListText = 'Choose List');
        this.marketUiFormControl.setValidators(hasAccess ? [Validators.required] : []);
      }),
    );

    this.filteredMarkets$ = combineLatest([
      this.marketSearchTerm$$.asObservable().pipe(startWith<string | Market>('')),
      this.marketService.markets.pipe(startWith([])),
    ]).pipe(
      map(([searchTerm, markets]: [string, Market[]]) =>
        markets.filter((market) => {
          this.selectFirstMarket = !!searchTerm;
          if (market) {
            return market.name.toLowerCase().indexOf(searchTerm.toLowerCase()) > -1;
          }
          return false;
        }),
      ),
    );

    this.filteredLists$ = combineLatest([
      this.listSearchTerm$$.asObservable().pipe(startWith<string | ActionList>('')),
      this.actionListsStoreService.actionLists$.pipe(startWith([])),
    ]).pipe(
      map(([searchTerm, actionLists]: [string, ActionList[]]) => {
        if (actionLists) {
          return actionLists.filter((actionList) => {
            this.selectFirstActionList = !!searchTerm;
            if (actionList && typeof searchTerm === 'string') {
              return actionList.name.toLowerCase().indexOf(searchTerm.toLowerCase()) > -1;
            }
            return false;
          });
        }
      }),
      startWith([]),
    );

    this.marketUiESM = new CustomErrorStateMatcher(
      this.marketListForm.controls.marketId,
      (formControl: AbstractControl) => !formControl.value && formControl.touched,
    );
    this.subscriptions.push(
      this.marketService.markets
        .pipe(
          map((markets) => {
            this.accessibleMarkets = markets.map((market) => market.market_id);
          }),
        )
        .subscribe(),
    );

    this.subscriptions.push(
      combineLatest([this.marketService.currentMarket$, this.hasAccessToMarkets$])
        .pipe(
          tap(([market, marketAccess]) => {
            if (marketAccess === false || this.accessibleMarkets.length === 1) {
              this.marketListForm.patchValue({ marketId: market });
            }
          }),
        )
        .subscribe(),
    );
    this.marketMatcher = new MyErrorStateMatcher();
    this.listMatcher = new MyErrorStateMatcher();
    this.permissionsFlag = true;
    this.notificationsFlag = true;
    this.welcomeEmailFlag = false;
    this.welcomeMessage = '';
    this.emailLanguage = this.emailLanguages[0].id;
    this.englishSelection = this.emailLanguage;

    this.partnerBusinessCenterName$ = this.partnerId$.pipe(
      switchMap((partnerId) => {
        return this.vConfigService.getConfigurations(partnerId, [this.vbcConfigKey]);
      }),
      map((config) => config.vbc_product_name),
      shareReplay(1),
    );
  }

  ngAfterViewInit(): void {
    this.dynamicOpenCloseService.registerTemplate('customFieldEditor', this.customFieldEditor);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  fileOnChange(event: any): void {
    this.trackingService.trackEvent(
      `BulkImport:clicked-upload-file-bulk-import`,
      'BulkImport',
      `clicked-upload-file-bulk-import`,
    );
    this.errorRows = [];
    if (!event.target.files) {
      return;
    }
    this.processing = true;
    this.processingErrorMessage = null;
    this.mappingService.resetMapping();
    this.bulkImportService.file = event.target.files[0];
    this.bulkImportService
      .processFile()
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.processing = false;
          this.pageStepper.next();
        },
        error: (error) => {
          this.processing = false;
          this.processingErrorMessage = error;
        },
      });
  }

  nameDisplayFn(objectWithAName: { name: string }): string {
    return objectWithAName ? objectWithAName.name : undefined;
  }

  selectMarket(event: MatAutocompleteSelectedEvent): void {
    this.marketListForm.controls.marketId.setValue(event.option.value);
    this.marketName = this.marketListForm.controls.marketId.value.name;
  }

  selectList(event: MatAutocompleteSelectedEvent): void {
    this.listSelected = true;
    this.marketListForm.controls.useExistingList.setValue(true);
    this.marketListForm.controls.actionListId.setValue(event.option.value);
    this.listName = this.marketListForm.controls.actionListId.value.name;
  }

  updateSearchTerm(subject$$: BehaviorSubject<string>, term: string): void {
    subject$$.next(term);
  }

  displayFn(actionList?: ActionList): string | undefined {
    return actionList ? actionList.name : undefined;
  }

  downloadTemplate(): void {
    window.open('/manage-accounts/import-list/csv-template/', '_blank');
  }

  downloadBusinessCategories(): void {
    window.open('/manage-accounts/import-list/business-categories-csv', '_blank');
  }

  increaseShow(): void {
    this.show += 10;
  }

  goToFinalStep(): void {
    this.netRows$ = combineLatest([this.bulkImportService.totalRows$, this.bulkImportService.errorRows$]).pipe(
      map(([totalRows, errorRows]) => {
        return totalRows - errorRows;
      }),
    );
    this.marketUiFormControl.markAsTouched();
    this.listFormControl.markAsTouched();
    this.setListName();
  }

  clearInput(event: any, input: any): void {
    this.show = 10;
    this.errorRows = [];
    input.value = '';
  }

  setListName(): void {
    if (this.marketListForm.controls.useExistingList.value && this.marketListForm.controls.actionListId.value) {
      this.listName = this.marketListForm.controls.actionListId.value.name;
    } else {
      this.listName = this.marketListForm.controls.actionListName.value;
    }
  }

  selectExistingList(): void {
    if (!this.marketListForm.controls.useExistingList.value) {
      this.marketListForm.controls.actionListId.setValidators([Validators.required]);
      this.listName = '';
      this.marketListForm.controls.actionListId.setValue('');
      this.marketListForm.controls.actionListName.clearValidators();
      this.marketListForm.controls.actionListName.updateValueAndValidity({ onlySelf: true });
      this.listText = `To an <strong>existing list</strong> named:`;
    }
  }

  createNewList(): void {
    this.marketListForm.controls.actionListName.setValidators([Validators.required]);
    this.listFormControl.setValue('');
    this.marketListForm.controls.actionListName.setValue(null);
    this.marketListForm.controls.actionListId.clearValidators();
    this.marketListForm.controls.actionListId.updateValueAndValidity({ onlySelf: true });
    this.listText = `To a <strong>new list</strong> named:`;
  }

  goToFieldMapping(): void {
    if (this.processingErrorMessage === null) {
      this.fileForm.controls.file.markAsTouched();
      this.fileForm.controls.fileHasErrors.markAsTouched();
      this.pageStepper.next();
    }
  }

  startListImport(): void {
    this.loading = true;
    this.bulkImportService
      .startListImport(
        this.bulkImportService.sessionId,
        this.listName,
        this.marketListForm.controls.marketId.value.market_id,
        this.overwriteBlank,
        this.permissionsFlag,
        this.notificationsFlag,
        this.welcomeEmailFlag,
        this.emailLanguage,
        this.welcomeMessage,
      )
      .subscribe((response) => {
        this.loading = false;
        this.router.navigate(['action-lists', response.data.actionListId, 'history']);
      });
  }

  openPreview(): void {
    this.dialog.open(PreviewWelcomeEmailComponent, { width: '600px' });
  }
}

export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

export class CustomErrorStateMatcher implements ErrorStateMatcher {
  hasError: (formControl: AbstractControl) => boolean;
  formControl: AbstractControl;

  constructor(formControl: AbstractControl, hasError: (formControl: AbstractControl) => boolean) {
    this.formControl = formControl;
    this.hasError = hasError;
  }

  isErrorState(): boolean {
    return this.hasError.call(this, this.formControl);
  }
}
